/* eslint-disable no-magic-numbers */
import mapValues from "lodash/mapValues";
import moment from "moment-timezone";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import { calculateWeekdays } from "@/lib/weekDays";
import OutcomeType from "@/constants/OutcomeType";
import ScheduleFilterType from "@/constants/Schedule/ScheduleFilterType";
import TeamType from "@/constants/TeamType";
import MeetingDisplayState from "@/constants/MeetingDisplayState";
import TopicOccurrenceType from "@/constants/TopicOccurrenceType";

export default {
	// flags
	isRefreshingSchedule: state => state.isRefreshingSchedule,
	/** TODO: topics are a meeting agenda store concern - move */
	isRequestingTopics: state => state.isRequestingTopics,
	/** TODO: topics are a meeting agenda store concern - move */
	isResortingTopics: state => state.isResortingTopics,
	/** TODO: topics are a meeting agenda store concern - move */
	isSavingTopics: state => state.isSavingTopics,
	isRefreshAllowed: state => state.isRefreshAllowed,
	isBackgroundUpdate: state => state.isBackgroundUpdate,
	/** TODO: topics are a meeting agenda store concern - move */
	isRemovingTopics: state => state.isRemovingTopics,
	isScheduleSyncing: state => state.isScheduleSyncing,
	hasScheduleSyncFailed: state => state.hasScheduleSyncFailed,
	isAssigningTeam: state => state.isAssigningTeam,
	/** TODO: commitments are a meeting agenda store concern - move */
	isFetchingMeetingCommitments: state => state.isFetchingMeetingCommitments,
	/** TODO: commitments are a meeting agenda store concern - move */
	isChangingCommitmentCompletion: state => state.isChangingCommitmentCompletion,
	isFetchingMeetingSeries: state => state.isFetchingMeetingSeries,
	isFetchingTeamGoals: state => state.isFetchingTeamGoals,
	isConnectingTeam: state => state.isConnectingTeam,
	isOverviewVideoModalOpen: state => state.isOverviewVideoModalOpen,

	// formats
	shortTimeFormat: _ => "h:mma", // 9:00am
	midDateFormat: _ => "dddd, MMMM D, YYYY", // Monday, April 20, 2020
	shortGoalDateFormat: _ => "MMM D, YYYY", // Apr 20 9:00am
	shortCommitmentDateFormat: _ => "MMM D", // Apr 20
	midCommitmentDateFormat: _ => "dddd, MMMM D, YYYY", // Monday, April 20, 2020

	// maps
	/** TODO: topics are a meeting agenda store concern - move */
	meetingTopicMap: state => state.meetingTopicMap,
	/** TODO: topics are a meeting agenda store concern - move */
	meetingTopicRequestMap: state => state.meetingTopicRequestMap,
	meetingSeriesMap: state => state.meetingSeriesMap,
	meetingSeriesOffsetMap: state => state.meetingSeriesOffsetMap,
	meetingPermissionsMap: state => state.meetingPermissionsMap,
	/** TODO: topics are a meeting agenda store concern - move */
	topicPermissionsMap: state => state.topicPermissionsMap,
	/** TODO: topics are a meeting agenda store concern - move */
	topicDeferralMap: state => state.topicDeferralMap,
	meetingInviteeMap: state => state.meetingInviteeMap,

	/** TODO: outcomes are a meeting agenda store concern - move */
	outcomeMap: state => state.outcomeMap,
	/** TODO: outcomes are a meeting agenda store concern - move */
	meetingOutcomes: state => meetingId => {
		const meetingOutcomeIds = state.meetingOutcomeIdMap[ meetingId ] || [];
		return meetingOutcomeIds.map( outcomeId => {
			return state.outcomeMap[ outcomeId ];
		} );
	},
	/** TODO: commitments are a meeting agenda store concern - move */
	meetingCommitmentMap: ( state, getters ) => meetingId => {
		return mapValues( getters.meetingOutcomes( meetingId ), outcomes => {
			return outcomes.filter( outcome => outcome.type === OutcomeType.COMMITMENT );
		} );
	},

	// counts
	meetingTopicCounts: state => {
		return mapValues( state.meetingTopicMap, topics => {
			return topics.filter( topic => !!topic.id ).length;
		} );
	}, // still needed in schedule store
	pinnedTopicCounts: state => {
		return mapValues( state.meetingTopicMap, topics => {
			const pinnedOccurrenceTypes = [ TopicOccurrenceType.PINNED, TopicOccurrenceType.PINNED_INSTANCE, ];
			return topics.filter( topic => {
				return !!topic.id &&
					pinnedOccurrenceTypes.includes( topic.occurrenceType );
			} ).length;
		} );
	}, // still needed in schedule store
	meetingCommitmentCounts: ( state, getters ) => {
		return mapValues(
			getters.meetingCommitmentMap,
			commitments => commitments.length
		);
	}, // still needed in schedule store

	// state
	persistableFilterOptions: state => state.persistableFilterOptions,
	scheduleFilterOptions: state => state.scheduleFilterOptions,
	selectedFilterOption: state => state.scheduleFilterOptions.find( x => x.value === state.selectedFilterOption ),
	selectedScheduleDate: state => state.selectedScheduleDate,
	selectedScheduleMonth: state => {
		return state.selectedScheduleDate ?
			state.selectedScheduleDate.format( "MMMM" ) :
			"";
	},
	selectedScheduleYear: state => {
		return state.selectedScheduleDate ?
			state.selectedScheduleDate.format( "YYYY" ) :
			"";
	},
	selectedWeekOffset: state => state.selectedWeekOffset,
	selectedWeekDates: state => {
		const { selectedScheduleDate, } = state;
		if ( !selectedScheduleDate ) {
			return [];
		}
		const targetDate = selectedScheduleDate.clone();
		return calculateWeekdays( targetDate );
	},
	selectedScheduleMeetingId: state => state.selectedScheduleMeetingId,
	selectedMeeting: state => {
		return state.meetings[ state.selectedScheduleMeetingId ] || null;
	},
	/**
	 * @returns {Array.<DailyMeetings>}
	 */
	weeklyMeetings: ( state, getters, rootState, rootGetters ) => {
		const timezone = rootGetters[ "auth/timezone" ];
		const today = moment.tz( timezone ).format( "YYYY-MM-DD" );
		const scheduleFilters = rootGetters.settings.scheduleFilters;
		// weekDays are in order
		const weekDays = getters.selectedWeekDates;
		let affectedByFilters = false;
		return weekDays.map( weekDay => {
			const shortDate = weekDay.shortFormat;
			const meetingIds = state.dateMeetingMap[ shortDate ] || [];
			const meetings = meetingIds.reduce( ( accum, meetingId ) => {
				const mtg = state.meetings[ meetingId ];
				const allMeetings = state.selectedFilterOption === ScheduleFilterType.ALL_MEETINGS;
				// team meeting filter & criteria
				const onlyTeamMeetings = state.selectedFilterOption === ScheduleFilterType.TEAM_MEETINGS;
				const isTeamMeeting = get( mtg, [ "teams", 0, "type", ] ) === TeamType.TEAM;
				// 1:1 meeting filter & criteria
				const onlyOneOnOneMeetings = state.selectedFilterOption === ScheduleFilterType.ONE_ON_ONE_MEETINGS;
				const isOneOnOneMeeting = get( mtg, [ "teams", 0, "type", ] ) === TeamType.ONE_ON_ONE;
				// "no topic" meeting filter & criteria
				const onlyNoTopicMeetings = state.selectedFilterOption === ScheduleFilterType.MEETINGS_WITH_NO_TOPICS;
				const isNoTopicMeeting = mtg.topics.length === 0;
				// "no team" meeting filter & criteria
				const onlyNoTeamMeetings = state.selectedFilterOption === ScheduleFilterType.MEETINGS_WITH_NO_TEAM;
				const isNoTeamMeeting = mtg.teams.length === 0;
				// "hidden" meeting filter & criteria
				const isHidden = mtg.displayState === MeetingDisplayState.HIDDEN;
				const isShowingHidden = scheduleFilters.showHiddenMeetings;
				// "events with only me" meeting filter & criteria
				const isShowingEventsWithOnlyMe = scheduleFilters.showEventsWithOnlyMe;
				const isAnEventWithOnlyMe = mtg.invitees.length === 1;
				// all day meeting filter & criteria
				const isShowingAllDayMeetings = scheduleFilters.showAllDayEvents;
				const isAllDayEvent = mtg.isAllDay;

				const itemPassesVolatileFilters = allMeetings ||
					( onlyTeamMeetings && isTeamMeeting ) ||
					( onlyOneOnOneMeetings && isOneOnOneMeeting ) ||
					( onlyNoTopicMeetings && isNoTopicMeeting ) ||
					( onlyNoTeamMeetings && isNoTeamMeeting );

				const passesHiddenFilters = ( !isShowingHidden && !isHidden ) || isShowingHidden;
				const passesOnlyMeFilters = ( !isShowingEventsWithOnlyMe && !isAnEventWithOnlyMe ) || isShowingEventsWithOnlyMe;
				const passesAllDayFilters = ( !isShowingAllDayMeetings && !isAllDayEvent ) || isShowingAllDayMeetings;

				const itemPassesPersistentFilters = passesHiddenFilters && passesOnlyMeFilters && passesAllDayFilters;

				if ( itemPassesVolatileFilters && itemPassesPersistentFilters ) {
					accum.push( mtg );
				} else {
					affectedByFilters = true;
				}

				return accum;
			}, [] );
			const orderedMeetings = orderBy( meetings, "plannedStart" );
			return {
				key: shortDate,
				day: weekDay.longFormat,
				meetings: orderedMeetings,
				isToday: weekDay.shortFormat === today,
				meetingsHiddenByFilters: affectedByFilters && meetingIds.length && !orderedMeetings.length,
			};
		} );
	},
	selectedMeetingTab: state => state.selectedMeetingTab,
	/** TODO: topics are a meeting agenda store concern - move */
	dirtyTopicFlagMap: state => state.dirtyTopicFlagMap,
	/** TODO: topics are a meeting agenda store concern - move */
	newTopic: state => state.newTopic,
};



// WEBPACK FOOTER //
// ./src/store/modules/schedule/getters.js